"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  defaultFilters,
  type MentorFilters,
} from "@/hooks/useMentorFilters";
import {
  defaultAgencyFilters,
  TEAM_SIZE_BUCKETS,
  type AgencyFilters,
  type TeamSizeBucket,
} from "@/hooks/useAgencyFilters";
import {
  defaultRepresentativeFilters,
  type RepresentativeFilters,
} from "@/hooks/useRepresentativeFilters";
import type { BrowseType } from "./TypePillToggle";
import type { SortKey } from "./SortDropdown";

const VALID_TYPES: ReadonlyArray<BrowseType> = ["all", "mentor", "agency", "representative"];
const VALID_SORTS: ReadonlyArray<SortKey> = [
  "top",
  "reviewed",
  "price-asc",
  "price-desc",
  "recent",
];

export type BrowseUrlState = {
  q: string;
  type: BrowseType;
  sort: SortKey;
  mentor: MentorFilters;
  agency: AgencyFilters;
  representative: RepresentativeFilters;
};

export function makeDefaultBrowseState(
  defaultType: BrowseType
): BrowseUrlState {
  return {
    q: "",
    type: defaultType,
    sort: "top",
    mentor: defaultFilters,
    agency: defaultAgencyFilters,
    representative: defaultRepresentativeFilters,
  };
}

// ── parsing helpers ─────────────────────────────────────────────────────────

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseTeamSizes(raw: string | null): TeamSizeBucket[] {
  return parseList(raw).filter((b): b is TeamSizeBucket =>
    (TEAM_SIZE_BUCKETS as readonly string[]).includes(b)
  );
}

function parseNumber(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function parseBool(raw: string | null): boolean {
  return raw === "1" || raw === "true";
}

function parseType(
  raw: string | null,
  fallback: BrowseType
): BrowseType {
  if (raw && (VALID_TYPES as readonly string[]).includes(raw)) {
    return raw as BrowseType;
  }
  return fallback;
}

function parseSort(raw: string | null): SortKey {
  if (raw && (VALID_SORTS as readonly string[]).includes(raw)) {
    return raw as SortKey;
  }
  return "top";
}

function readStateFromParams(
  params: URLSearchParams,
  defaultType: BrowseType
): BrowseUrlState {
  const minRatingMentor = parseNumber(params.get("minRating"));
  const minRatingAgency = minRatingMentor; // shared key
  const maxRate = parseNumber(params.get("maxRate"));
  const minReviews = parseNumber(params.get("minReviews"));

  const mentor: MentorFilters = {
    countries: parseList(params.get("countries")),
    fields: parseList(params.get("fields")),
    minRating: minRatingMentor ?? defaultFilters.minRating,
    onlyAvailable: parseBool(params.get("available")),
    maxHourlyRate: maxRate, // null when absent → no cap
    hideMentorsWithoutRate: parseBool(params.get("hideNoRate")),
    minReviews: minReviews ?? defaultFilters.minReviews,
  };

  const agency: AgencyFilters = {
    countries: parseList(params.get("countries")),
    fields: parseList(params.get("fields")),
    services: parseList(params.get("services")),
    minRating: minRatingAgency ?? defaultAgencyFilters.minRating,
    teamSizes: parseTeamSizes(params.get("teamSizes")),
  };

  const representative: RepresentativeFilters = {
    countries: parseList(params.get("countries")),
    fields: parseList(params.get("fields")),
    institutionTypes: parseList(params.get("repTypes")),
    services: parseList(params.get("repServices")),
  };

  return {
    q: params.get("q") ?? "",
    type: parseType(params.get("type"), defaultType),
    sort: parseSort(params.get("sort")),
    mentor,
    agency,
    representative,
  };
}

// ── serialization ───────────────────────────────────────────────────────────

/** Build a query string containing only non-default values. */
function serializeState(
  state: BrowseUrlState,
  defaultType: BrowseType
): string {
  const params = new URLSearchParams();

  if (state.q.trim() !== "") params.set("q", state.q.trim());
  if (state.type !== defaultType) params.set("type", state.type);
  if (state.sort !== "top") params.set("sort", state.sort);

  if (state.type === "representative") {
    if (state.representative.countries.length > 0) {
      params.set("countries", state.representative.countries.join(","));
    }
    if (state.representative.fields.length > 0) {
      params.set("fields", state.representative.fields.join(","));
    }
    if (state.representative.institutionTypes.length > 0) {
      params.set("repTypes", state.representative.institutionTypes.join(","));
    }
    if (state.representative.services.length > 0) {
      params.set("repServices", state.representative.services.join(","));
    }
  } else {
    // Country/field filters — type-aware. In "all" mode we pull from the
    // mentor slice (it mirrors agency's because the BrowseList keeps them
    // in sync for the shared filter UI).
    const activeFilters =
      state.type === "agency" ? state.agency : state.mentor;

    if (activeFilters.countries.length > 0) {
      params.set("countries", activeFilters.countries.join(","));
    }
    if (activeFilters.fields.length > 0) {
      params.set("fields", activeFilters.fields.join(","));
    }
    if (activeFilters.minRating > 0) {
      params.set("minRating", String(activeFilters.minRating));
    }

    // Mentor-only filters
    if (state.type === "mentor") {
      if (
        state.mentor.maxHourlyRate !== null &&
        state.mentor.maxHourlyRate !== defaultFilters.maxHourlyRate
      ) {
        params.set("maxRate", String(state.mentor.maxHourlyRate));
      }
      if (state.mentor.onlyAvailable) params.set("available", "1");
      if (state.mentor.hideMentorsWithoutRate) params.set("hideNoRate", "1");
      if (state.mentor.minReviews > 0) {
        params.set("minReviews", String(state.mentor.minReviews));
      }
    }

    // Agency-only filters
    if (state.type === "agency") {
      if (state.agency.services.length > 0) {
        params.set("services", state.agency.services.join(","));
      }
      if (state.agency.teamSizes.length > 0) {
        params.set("teamSizes", state.agency.teamSizes.join(","));
      }
    }
  }

  return params.toString();
}

// ── public hook ─────────────────────────────────────────────────────────────

export type UseBrowseUrlStateReturn = {
  state: BrowseUrlState;
  setQ: (q: string) => void;
  setType: (type: BrowseType) => void;
  setSort: (sort: SortKey) => void;
  setMentorFilters: (
    update:
      | Partial<MentorFilters>
      | ((prev: MentorFilters) => MentorFilters)
  ) => void;
  setAgencyFilters: (
    update:
      | Partial<AgencyFilters>
      | ((prev: AgencyFilters) => AgencyFilters)
  ) => void;
  setRepresentativeFilters: (
    update:
      | Partial<RepresentativeFilters>
      | ((prev: RepresentativeFilters) => RepresentativeFilters)
  ) => void;
  /** Reset filters for the current type (search/sort/type preserved). */
  clearCurrentFilters: () => void;
  /** Reset everything except `type` to defaults. */
  clearAll: () => void;
};

export function useBrowseUrlState(
  defaultType: BrowseType
): UseBrowseUrlStateReturn {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Seed state ONCE from the initial URL. After that, the hook owns
  // state and writes back to the URL — we don't keep re-reading from
  // useSearchParams (which would fight our own writes).
  const [state, setState] = useState<BrowseUrlState>(() =>
    readStateFromParams(
      new URLSearchParams(searchParams?.toString() ?? ""),
      defaultType
    )
  );

  // Track the last query string we wrote so we can ignore the echo
  // from Next router updating searchParams.
  const lastWrittenRef = useRef<string>(serializeState(state, defaultType));

  // Mirror state back to URL whenever it changes.
  useEffect(() => {
    const qs = serializeState(state, defaultType);
    if (qs === lastWrittenRef.current) return;
    lastWrittenRef.current = qs;
    const url = qs ? `${pathname}?${qs}` : pathname;
    router.replace(url, { scroll: false });
  }, [state, defaultType, pathname, router]);

  // ── setters ───────────────────────────────────────────────────────────────

  const setQ = useCallback((q: string) => {
    setState((prev) => (prev.q === q ? prev : { ...prev, q }));
  }, []);

  const setType = useCallback((type: BrowseType) => {
    setState((prev) => (prev.type === type ? prev : { ...prev, type }));
  }, []);

  const setSort = useCallback((sort: SortKey) => {
    setState((prev) => (prev.sort === sort ? prev : { ...prev, sort }));
  }, []);

  const setMentorFilters = useCallback(
    (
      update:
        | Partial<MentorFilters>
        | ((prev: MentorFilters) => MentorFilters)
    ) => {
      setState((prev) => {
        const nextMentor =
          typeof update === "function"
            ? update(prev.mentor)
            : { ...prev.mentor, ...update };

        // Keep shared keys (countries, fields, minRating) in sync with
        // the agency slice so the unified "All" filter UI behaves
        // predictably and round-trips through the URL.
        const nextAgency: AgencyFilters = {
          ...prev.agency,
          countries: nextMentor.countries,
          fields: nextMentor.fields,
          minRating: nextMentor.minRating,
        };
        return { ...prev, mentor: nextMentor, agency: nextAgency };
      });
    },
    []
  );

  const setAgencyFilters = useCallback(
    (
      update:
        | Partial<AgencyFilters>
        | ((prev: AgencyFilters) => AgencyFilters)
    ) => {
      setState((prev) => {
        const nextAgency =
          typeof update === "function"
            ? update(prev.agency)
            : { ...prev.agency, ...update };

        const nextMentor: MentorFilters = {
          ...prev.mentor,
          countries: nextAgency.countries,
          fields: nextAgency.fields,
          minRating: nextAgency.minRating,
        };
        return { ...prev, mentor: nextMentor, agency: nextAgency };
      });
    },
    []
  );

  const setRepresentativeFilters = useCallback(
    (
      update:
        | Partial<RepresentativeFilters>
        | ((prev: RepresentativeFilters) => RepresentativeFilters)
    ) => {
      setState((prev) => {
        const next =
          typeof update === "function"
            ? update(prev.representative)
            : { ...prev.representative, ...update };
        return { ...prev, representative: next };
      });
    },
    []
  );

  const clearCurrentFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mentor: defaultFilters,
      agency: defaultAgencyFilters,
      representative: defaultRepresentativeFilters,
    }));
  }, []);

  const clearAll = useCallback(() => {
    setState((prev) => ({
      ...makeDefaultBrowseState(defaultType),
      type: prev.type,
    }));
  }, [defaultType]);

  return useMemo(
    () => ({
      state,
      setQ,
      setType,
      setSort,
      setMentorFilters,
      setAgencyFilters,
      setRepresentativeFilters,
      clearCurrentFilters,
      clearAll,
    }),
    [
      state,
      setQ,
      setType,
      setSort,
      setMentorFilters,
      setAgencyFilters,
      setRepresentativeFilters,
      clearCurrentFilters,
      clearAll,
    ]
  );
}
